import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ColumnFiltersState,
  PaginationState,
  SortingState,
  createColumnHelper,
} from "@tanstack/react-table";
import { FormEvent, useCallback, useContext, useMemo, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { Link, useSearchParams } from "react-router-dom";
import type { UnpackResponse } from "../../../api/src/lib";
import type { RebalancesController } from "../../../api/src/rebalances/rebalances.controller";
import Loading from "../Loading";
import FormError from "../components/FormError";
import { NotificationContext } from "../Notifications";
import ActionButton from "../components/ActionButton";
import TabContainerWithTabs from "../components/TabContainer";
import { RowExpander } from "../components/Table/RowExpander";
import { Table, metaDefault, useTable } from "../components/Table/Table";
import {
  AssignedToFilter,
  multiSelectIncludes,
} from "../components/Table/filters";
import {
  onColumnFiltersChange,
  onPaginationChange,
  onSortingChange,
  useTableSettings,
} from "../components/Table/tableSettings";
import { useAuthenticatedPercentFetch } from "../clients/percents";
import {
  processEmptyResponse,
  refusalSentence,
  useAuthenticatedMutation,
  useAuthenticatedMutationAsync,
} from "../lib/api";
import { pendingLabel } from "../lib/display";
import { formatCurrency } from "../lib/numbers";
import RebalanceQueryControls from "./RebalanceQueryControls";
import { validateRqlQuery } from "../lib/rql";
import RebalanceActionButtons from "./RebalanceActionButtons";
import RebalanceListSummary from "./RebalanceListSummary";
import RebalancesNav from "./RebalancesNav";
import { deserializeRebalanceListPercents } from "./percents";
import {
  getRebalanceRowId,
  getRebalanceSubRows,
  useRebalanceListRows,
} from "./useRebalanceListRows";
import {
  RebalanceReasonFilter,
  RebalanceRow,
  getRealizedGainLoss,
  getRiskAllocationDisplay,
  getSelectedRebalanceRows,
  rebalanceTypeDisplay,
  rebalanceTypeFilterOptions,
} from "./lib";

const columnHelper = createColumnHelper<RebalanceRow>();

// APP-1280: for tolerance rebalances, state which band was breached and by how
// much, rather than just "Tolerance". `reason` carries the band (e.g. "Risk
// Allocation"); `reasonChange` is the drift — a fraction for allocation bands,
// a dollar amount for the "Cash" trigger. Returns null when there's nothing to
// add (non-tolerance, or no reason on the row).
function toleranceBandSubLabel(row: RebalanceRow): string | null {
  if (row.type !== "tolerance" || !row.reason) {
    return null;
  }
  const band = row.reason;
  const change = row.reasonChange;
  if (change === undefined || change === null || change <= 0) {
    return band;
  }
  // Allocation bands track drift as a fraction; the Cash trigger is a dollar gap.
  const drift = /Allocation$/.test(band)
    ? `${(change * 100).toFixed(1)}% over band`
    : `${formatCurrency(change)} over trigger`;
  return `${band} · ${drift}`;
}

// APP-1280: drift magnitude for the Drift column. Allocation bands track drift
// as a fraction (shown as %); the Cash trigger is a dollar gap.
function formatDrift(row: RebalanceRow): string {
  const change = row.reasonChange;
  if (change === undefined || change === null || change <= 0) {
    return "—";
  }
  return row.type === "tolerance" && /Allocation$/.test(row.reason ?? "")
    ? `${(change * 100).toFixed(1)}%`
    : formatCurrency(change);
}

const RebalancesToday = () => {
  const [page] = useState(1);
  const [pageSize] = useState(1000); // APP-1308: show all same-day proposals on one page (restore pre-#984 behavior); server pager is the proper follow-up
  const [searchParams] = useSearchParams();

  // APP-1279: seed the filter from ?q= so the insights "X Households with …"
  // links open the list pre-filtered (and the filter is shareable via URL). A
  // deep link takes precedence over any previously-selected board.
  const [rqlQuery, setRqlQuery] = useState(() => {
    const q = searchParams.get("q");
    if (q) {
      localStorage.removeItem("rebalances-selected-board-id");
      return q;
    }
    return "";
  });

  // Handle RQL query changes
  const handleRqlQueryChange = (query: string) => {
    setRqlQuery(query);
  };

  // APP-1279/APP-1280: filtering runs server-side (RQL sent as &q=); the list
  // is ordered by drift (reason_change) desc on the server too. Only a valid
  // query is forwarded.
  const rqlValidation = validateRqlQuery(rqlQuery);
  // APP-1652: `GET /rebalances/filter` is served at `GET /v1/rebalances` under
  // `limit`/`offset`. One page of `pageSize` rows, as before (APP-1308).
  const queryKey = `/v1/rebalances?status=requested,proposed&metrics=true&limit=${pageSize}&offset=${(page - 1) * pageSize}&sort=reason_change:desc${
    rqlQuery.trim() && rqlValidation.success
      ? `&q=${encodeURIComponent(rqlQuery)}`
      : ""
  }`;

  const {
    isPending: isPendingRebalances,
    isError: isErrorRebalances,
    data: dataRebalances,
  } = useAuthenticatedPercentFetch<
    UnpackResponse<RebalancesController["getTodaySuggested"]>
  >(queryKey, deserializeRebalanceListPercents);
  const rebalances = dataRebalances?.data;

  const [tableSettings, setTableSettings] =
    useTableSettings("rebalances-today");

  const {
    rebalanceRows,
    isPendingAssignees,
    filterBarItems,
    setRowSelectionRef,
  } = useRebalanceListRows(rebalances, tableSettings, setTableSettings);

  // APP-1486, swap spec R62/R63: a proposal a position swap owns is approved
  // or cancelled only from its swap, and the API refuses the PATCH with 409,
  // which would sink every other proposal of a Bulk Approve. Its rows (and
  // its account rows) can't be selected and it has no row actions.
  const swapRebalanceIds = useMemo(
    () =>
      new Set(
        (rebalances ?? [])
          .filter((rebalance) => rebalance.positionSwapId != null)
          .map((rebalance) => rebalance.id),
      ),
    [rebalances],
  );

  const queryClient = useQueryClient();

  // APP-1486, swap spec R120: the API refuses an action on a position swap's
  // proposal with 409 and a sentence naming the swap. A list a later write
  // made out of date can still send such a proposal, so the refusal is shown.
  const { pushNotification } = useContext(NotificationContext);
  const notifyError = useCallback(
    (header: string) => (error: unknown) =>
      pushNotification({
        id: "rebalances-today-error",
        header,
        body: refusalSentence(error) ?? "Something went wrong; try again.",
        variant: "danger",
      }),
    [pushNotification],
  );

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "select",
        header: (info) => (
          <Form.Check
            checked={info.table.getIsAllRowsSelected()}
            onChange={info.table.getToggleAllPageRowsSelectedHandler()}
          />
        ),
        aggregatedCell: (info) => (
          <Form.Check
            id={`select-${info.row.original.rebalanceId}`}
            checked={info.row.getIsSelected()}
            disabled={!info.row.getCanSelect()}
            onChange={info.row.getToggleSelectedHandler()}
          />
        ),
      }),
      columnHelper.accessor("name", {
        cell: (info) => {
          if (info.row.original.level === "household") {
            return (
              <>
                {info.row.getCanExpand() ? <RowExpander row={info.row} /> : ""}{" "}
                <Link to={`/rebalances/${info.row.original.rebalanceId}`}>
                  {info.getValue()}
                </Link>
              </>
            );
          }
          return (
            <div
              style={{
                paddingLeft: `${info.row.depth * 2}rem`,
              }}
            >
              {info.getValue()}
            </div>
          );
        },
        aggregatedCell: (info) => (
          <>
            {info.row.getCanExpand() ? <RowExpander row={info.row} /> : ""}{" "}
            <Link to={`/rebalances/${info.row.original.rebalanceId}`}>
              {info.getValue()}
            </Link>
          </>
        ),
        header: () => "Household",
        minSize: 225,
        filterFn: (row, columnId, filterValue) => {
          if (!filterValue || typeof filterValue !== "string") {
            return true;
          }

          const lowercaseFilter = filterValue.toLowerCase();
          const rowData = row.original;

          // For household rows, check if household name matches
          if (rowData.level === "household") {
            return rowData.name.toLowerCase().includes(lowercaseFilter);
          }

          // For account rows, always show them if their parent household matches
          // This ensures hierarchical consistency - if household matches, show all accounts
          return true;
        },
      }),
      columnHelper.accessor(
        (row: RebalanceRow) => {
          if (typeof row.type === "undefined") {
            return "";
          } else if (
            row.type &&
            Object.prototype.hasOwnProperty.call(rebalanceTypeDisplay, row.type)
          ) {
            const option = rebalanceTypeFilterOptions.find(
              (a) =>
                a.label ===
                rebalanceTypeDisplay[
                  row.type as keyof typeof rebalanceTypeDisplay
                ],
            );
            return option?.value.toString() ?? "";
          }
          return "";
        },
        {
          id: "type",
          header: () => "Trade Proposal Reason",
          cell: (info) => {
            const option = rebalanceTypeFilterOptions.find(
              (a) => a.value.toString() === info.getValue(),
            );
            const label = option?.label ?? "";
            const subLabel = toleranceBandSubLabel(info.row.original);
            return subLabel ? (
              <div>
                <div>{label}</div>
                <div className="text-muted small">{subLabel}</div>
              </div>
            ) : (
              label
            );
          },
          aggregatedCell: (info) => {
            const option = rebalanceTypeFilterOptions.find(
              (a) => a.value.toString() === info.getValue(),
            );
            const label = option?.label ?? "";
            const subLabel = toleranceBandSubLabel(info.row.original);
            return subLabel ? (
              <div>
                <div>{label}</div>
                <div className="text-muted small">{subLabel}</div>
              </div>
            ) : (
              label
            );
          },
          filterFn: multiSelectIncludes,
          meta: {
            filterComponent: RebalanceReasonFilter,
          },
        },
      ),
      // APP-1280 #2: drift column. `reasonChange` is the breach magnitude past
      // the band — the signal for "most likely to be acted on". Used as the
      // default sort (desc) so the largest breaches surface on top.
      columnHelper.accessor((row) => row.reasonChange ?? 0, {
        id: "drift",
        header: () => "Drift",
        enableColumnFilter: false,
        cell: (info) => formatDrift(info.row.original),
        aggregatedCell: (info) => formatDrift(info.row.original),
        meta: { className: "text-nowrap" },
      }),
      columnHelper.accessor((row) => row.assignedUser?.id.toString() ?? "", {
        id: "assignedUserId",
        cell: (info) => info.row.original.assignedUser?.name ?? "",
        aggregatedCell: (info) => info.row.original.assignedUser?.name ?? "",
        header: () => "Assigned To",
        minSize: 100,
        filterFn: multiSelectIncludes,
        meta: {
          filterComponent: AssignedToFilter,
        },
      }),
      columnHelper.accessor((row) => row.riskAllocation?.current.growth ?? 0, {
        id: "current",
        cell: (info) =>
          getRiskAllocationDisplay(info.row.original.riskAllocation?.current),
        aggregatedCell: (info) =>
          getRiskAllocationDisplay(info.row.original.riskAllocation?.current),
        header: () => "Current Allocation",
        enableColumnFilter: false,
        enableSorting: false,
        meta: { className: "text-nowrap" },
      }),
      columnHelper.accessor(
        (row) => row.riskAllocation?.postRebalance.growth ?? 0,
        {
          id: "postRebalance",
          cell: (info) =>
            info.row.original.status === "requested"
              ? pendingLabel
              : getRiskAllocationDisplay(
                  info.row.original.riskAllocation?.postRebalance,
                ),
          aggregatedCell: (info) =>
            info.row.original.status === "requested"
              ? pendingLabel
              : getRiskAllocationDisplay(
                  info.row.original.riskAllocation?.postRebalance,
                ),
          header: () => "Post-Trade Proposal Allocation",
          enableColumnFilter: false,
          enableSorting: false,
          meta: { className: "text-nowrap" },
        },
      ),
      columnHelper.accessor((row) => row.riskAllocation?.target.growth ?? 0, {
        id: "target",
        cell: (info) =>
          info.row.original.status === "requested"
            ? pendingLabel
            : getRiskAllocationDisplay(
                info.row.original.riskAllocation?.target,
              ),
        aggregatedCell: (info) =>
          info.row.original.status === "requested"
            ? pendingLabel
            : getRiskAllocationDisplay(
                info.row.original.riskAllocation?.target,
              ),
        header: () => "Target Allocation",
        enableColumnFilter: false,
        enableSorting: false,
        meta: { className: "text-nowrap" },
      }),
      columnHelper.accessor((row) => row.totalBalance ?? 0, {
        id: "totalBalance",
        cell: (info) => formatCurrency(info.row.original.totalBalance),
        aggregatedCell: (info) =>
          formatCurrency(info.row.original.totalBalance),
        header: () => "AUM",
        enableColumnFilter: false,
        meta: {
          headerClassName: "text-end",
          className: "text-end",
        },
      }),
      columnHelper.accessor((row) => row.cashBalance ?? 0, {
        id: "cashBalance",
        cell: (info) => formatCurrency(info.row.original.cashBalance),
        aggregatedCell: (info) => formatCurrency(info.row.original.cashBalance),
        header: () => "Cash",
        enableColumnFilter: false,
        meta: {
          headerClassName: "text-end",
          className: "text-end",
        },
      }),
      columnHelper.accessor((row) => row.buyTotal ?? 0, {
        id: "buyTotal",
        cell: (info) =>
          info.row.original.status === "requested"
            ? pendingLabel
            : formatCurrency(info.getValue()),
        aggregatedCell: (info) =>
          info.row.original.status === "requested"
            ? pendingLabel
            : formatCurrency(info.getValue()),
        header: () => "Buy",
        enableColumnFilter: false,
        meta: {
          headerClassName: "text-end",
          className: "text-end",
        },
      }),
      columnHelper.accessor((row) => row.sellTotal ?? 0, {
        id: "sellTotal",
        cell: (info) =>
          info.row.original.status === "requested"
            ? pendingLabel
            : formatCurrency(info.getValue()),
        aggregatedCell: (info) =>
          info.row.original.status === "requested"
            ? pendingLabel
            : formatCurrency(info.getValue()),
        header: () => "Sell",
        enableColumnFilter: false,
        meta: {
          headerClassName: "text-end",
          className: "text-end",
        },
      }),
      columnHelper.accessor(
        (row) => (row.sellTotal ?? 0) - (row.buyTotal ?? 0),
        {
          id: "net",
          cell: (info) =>
            info.row.original.status === "requested"
              ? pendingLabel
              : formatCurrency(info.getValue()),
          aggregatedCell: (info) =>
            info.row.original.status === "requested"
              ? pendingLabel
              : formatCurrency(info.getValue()),
          header: () => "Net",
          enableColumnFilter: false,
          meta: {
            headerClassName: "text-end",
            className: "text-end",
          },
        },
      ),
      columnHelper.accessor((row) => getRealizedGainLoss(row) ?? 0, {
        id: "realizedGainLoss",
        cell: (info) =>
          info.row.original.status === "requested"
            ? pendingLabel
            : formatCurrency(getRealizedGainLoss(info.row.original)),
        aggregatedCell: (info) =>
          info.row.original.status === "requested"
            ? pendingLabel
            : formatCurrency(getRealizedGainLoss(info.row.original)),
        header: () => "Realized Gain/Loss",
        enableColumnFilter: false,
        meta: {
          headerClassName: "text-end",
          className: "text-end",
        },
      }),
      columnHelper.display({
        id: "actions",
        meta: {
          className: "text-nowrap",
        },
        aggregatedCell: (info) => {
          const rebalance = info.row.original;

          // eslint-disable-next-line react-hooks/rules-of-hooks
          const approveRebalance = useAuthenticatedMutation<
            UnpackResponse<RebalancesController["changeRebalancesState"]>
          >(
            "/rebalances",
            {
              method: "PATCH",
              body: JSON.stringify({
                state: "approved",
                rebalanceIds: [rebalance.rebalanceId],
              }),
            },
            processEmptyResponse,
          );

          // eslint-disable-next-line react-hooks/rules-of-hooks
          const cancelRebalance = useAuthenticatedMutation<
            UnpackResponse<RebalancesController["changeRebalancesState"]>
          >(
            "/rebalances",
            {
              method: "PATCH",
              body: JSON.stringify({
                state: "canceled",
                rebalanceIds: [rebalance.rebalanceId],
              }),
            },
            processEmptyResponse,
          );

          // eslint-disable-next-line react-hooks/rules-of-hooks
          const approve = useMutation({
            mutationFn: async () => {
              await approveRebalance();

              const remainingRebalances = rebalances?.filter(
                (r) => r.id !== rebalance.rebalanceId,
              );

              queryClient.setQueryData([queryKey], {
                data: remainingRebalances,
              });
            },
            onError: notifyError("Approve failed"),
          });

          // eslint-disable-next-line react-hooks/rules-of-hooks
          const cancel = useMutation({
            mutationFn: async () => {
              await cancelRebalance();

              const remainingRebalances = rebalances?.filter(
                (r) => r.id !== rebalance.rebalanceId,
              );

              queryClient.setQueryData([queryKey], {
                data: remainingRebalances,
              });
            },
            onError: notifyError("Cancel failed"),
          });

          if (rebalance.positionSwapId != null) {
            return (
              <Link
                to={`/rebalances/position-swaps/${rebalance.positionSwapId}`}
              >
                Position swap
              </Link>
            );
          }

          return (
            <>
              {rebalance.status === "requested" ? null : (
                <ActionButton
                  variant="icon"
                  icon="/icons/approve.svg"
                  label="Approve"
                  onClick={() => approve.mutate()}
                  disabled={approve.isPending}
                  type="button"
                  className="me-2"
                />
              )}
              <ActionButton
                variant="icon"
                icon="/icons/trash.svg"
                label="Cancel"
                onClick={() => cancel.mutate()}
                disabled={cancel.isPending}
                type="button"
              />
            </>
          );
        },
      }),
    ],
    [queryClient, rebalances, queryKey, notifyError],
  );

  const rejectRow = useCallback(
    (id: number) => {
      const remainingRebalances = rebalances?.filter(
        (rebalance) => rebalance.id !== id,
      );

      queryClient.setQueryData([queryKey], {
        data: remainingRebalances,
      });
    },
    [queryClient, rebalances, queryKey],
  );

  const approveRow = useCallback(
    (id: number) => {
      const remainingRebalances = rebalances?.filter(
        (rebalance) => rebalance.id !== id,
      );

      queryClient.setQueryData([queryKey], {
        data: remainingRebalances,
      });
    },
    [queryClient, rebalances, queryKey],
  );

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
    tableSettings.filters,
  );
  // APP-1280 #2: default to drift desc (largest band breach on top) on first
  // load, while still honouring a sort the user has chosen and persisted.
  const [sorting, setSorting] = useState<SortingState>(
    tableSettings.sorting.length
      ? tableSettings.sorting
      : [{ id: "drift", desc: true }],
  );
  const [pagination, setPagination] = useState<PaginationState>({
    pageSize: tableSettings.pageSize,
    pageIndex: 0,
  });

  const { table, rowSelection, setRowSelection } = useTable({
    columns,
    data: rebalanceRows,
    getRowId: getRebalanceRowId,
    getSubRows: getRebalanceSubRows,
    enableRowSelection: (row) =>
      !swapRebalanceIds.has(row.original.rebalanceId),
    state: { columnFilters, sorting, pagination },
    onColumnFiltersChange: onColumnFiltersChange(
      columnFilters,
      setColumnFilters,
      tableSettings,
      setTableSettings,
    ),
    onSortingChange: onSortingChange(
      sorting,
      setSorting,
      tableSettings,
      setTableSettings,
    ),
    onPaginationChange: onPaginationChange(
      pagination,
      setPagination,
      tableSettings,
      setTableSettings,
    ),
    autoResetPageIndex: true,
    meta: {
      ...metaDefault,
      approveRow,
      deleteRow: rejectRow,
    },
  });
  setRowSelectionRef.current = setRowSelection;

  const approveRebalances = useAuthenticatedMutationAsync<
    UnpackResponse<RebalancesController["changeRebalancesState"]>
  >(
    "/rebalances",
    async () => {
      const rebalanceIds = getSelectedRebalanceRows(rowSelection).filter(
        (id) => !swapRebalanceIds.has(id),
      );

      return {
        method: "PATCH",
        body: JSON.stringify({
          state: "approved",
          rebalanceIds,
        }),
      };
    },
    processEmptyResponse,
  );

  const mutation = useMutation({
    mutationFn: async (event: FormEvent) => {
      event.preventDefault();

      // Get these before the async request for data consistency
      const selectedRebalanceIds = getSelectedRebalanceRows(rowSelection);
      const unselectedRebalances = rebalances?.filter(
        (rebalance) => !selectedRebalanceIds.includes(rebalance.id),
      );

      queryClient.setQueryData([queryKey], {
        data: unselectedRebalances,
      });

      await approveRebalances();
    },
    onError: notifyError("Bulk Approve failed"),
  });

  return (
    <>
      <Row>
        <Col>
          <RebalanceActionButtons />
        </Col>
      </Row>
      <TabContainerWithTabs tabs={RebalancesNav}>
        {isPendingRebalances || isPendingAssignees ? (
          <Loading />
        ) : (
          <>
            {/* R48: a failed read shows its error in place of the table, and
                no summary or "Bulk Approve" for rows not shown. The query stays,
                so a change of it reads again. */}
            {!isErrorRebalances && (
              <Form onSubmit={mutation.mutate}>
                <RebalanceListSummary rebalances={rebalanceRows ?? []} />
                <Row className="mb-3">
                  <Col md={6}>
                    <ActionButton
                      label="Bulk Approve"
                      icon="/icons/approve.svg"
                      variant="secondary"
                      type="submit"
                      disabled={mutation.isPending}
                    />
                  </Col>
                </Row>
              </Form>
            )}
            <RebalanceQueryControls
              query={rqlQuery}
              onQueryChange={handleRqlQueryChange}
            />
            {isErrorRebalances ? (
              <FormError message="Failed to load the trade proposals." />
            ) : (
              <Table table={table} filterBarItems={filterBarItems} />
            )}
          </>
        )}
      </TabContainerWithTabs>
    </>
  );
};

export default RebalancesToday;
